import type { ProgressData } from '@dcs/contracts';
import type { KeyValueStorage } from '../ports/storage.ts';

/**
 * What the sync keeps in the storage port next to the local progress records
 * (`<ns>:progress:v1:<class>`, owned by `createLocalProgressRepo`). Every helper swallows storage
 * errors: a browser that refuses to store simply syncs with less memory.
 *
 *  - `<ns>:progress-owner`: id of the user the local records belong to; absent = guest data. It
 *    decides at sign-in whether the local records may be uploaded (privacy, see `sync-repo.ts`).
 *  - `<ns>:progress-sync:v1:<class>`: `SyncMarks` of the class (tombstones, last PUT time).
 *  - `<ns>:progress-stash:v1`: `ProgressStash`, ONE slot for the whole device: the dirty classes a
 *    user had not uploaded when their session ended without an explicit sign-out (expiry). Never
 *    written on an explicit sign-out. Read back only by that same user's next sign-in, deleted when
 *    another user claims the device, by `purgeProgressForUser` and after `STASH_TTL_MS` (7 days).
 *    One slot is enough: a second user signing in deletes the first one's stash.
 */

export interface SyncMarks {
  /** Last reset of the class (epoch ms): see `ProgressData.resetAt`. */
  resetAt?: number;
  /** Last "clear the wrong list" (epoch ms): see `ProgressData.wrongClearedAt`. */
  wrongClearedAt?: number;
  /** When the last PUT of the class started, so the 30 s spacing survives a reload. */
  lastPutAt?: number;
}

export const ownerKey = (namespace: string): string => `${namespace}:progress-owner`;
export const marksKey = (licenseClass: string, namespace: string): string =>
  `${namespace}:progress-sync:v1:${licenseClass}`;
export const stashKey = (namespace: string): string => `${namespace}:progress-stash:v1`;

/** How long a session-expiry stash is kept (7 days). */
export const STASH_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** What a session that ended without a sign-out had not uploaded yet. */
export interface ProgressStash {
  userId: string;
  /** When it was written (epoch ms, injected clock): the TTL counts from here. */
  savedAt: number;
  /** Dirty classes only: the class record (contract shape) and its last PUT time. */
  classes: Record<string, { data: ProgressData; lastPutAt?: number }>;
}

export function readKey(storage: KeyValueStorage, key: string): string | null {
  try {
    return storage.get(key);
  } catch {
    return null;
  }
}

export function writeKey(storage: KeyValueStorage, key: string, value: string): void {
  try {
    storage.set(key, value);
  } catch {
    // Not kept; the sync works without it for this page.
  }
}

export function removeKey(storage: KeyValueStorage, key: string): void {
  try {
    storage.remove(key);
  } catch {
    // Nothing durable to remove.
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isTime = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

export function readMarks(
  storage: KeyValueStorage,
  licenseClass: string,
  namespace: string,
): SyncMarks {
  const text = readKey(storage, marksKey(licenseClass, namespace));
  if (text === null) return {};
  try {
    const raw: unknown = JSON.parse(text);
    if (!isRecord(raw)) return {};
    const marks: SyncMarks = {};
    if (isTime(raw.resetAt)) marks.resetAt = raw.resetAt;
    if (isTime(raw.wrongClearedAt)) marks.wrongClearedAt = raw.wrongClearedAt;
    if (isTime(raw.lastPutAt)) marks.lastPutAt = raw.lastPutAt;
    return marks;
  } catch {
    return {};
  }
}

export function writeMarks(
  storage: KeyValueStorage,
  licenseClass: string,
  namespace: string,
  marks: SyncMarks,
): void {
  writeKey(storage, marksKey(licenseClass, namespace), JSON.stringify(marks));
}

/** A stored `ProgressData`, checked by shape without zod; `null` when unusable. */
export function parseProgressData(raw: unknown): ProgressData | null {
  try {
    if (!isRecord(raw) || raw.v !== 1 || !isRecord(raw.done) || !Array.isArray(raw.wrong)) {
      return null;
    }
    if (!isTime(raw.updatedAt)) return null;
    const done: ProgressData['done'] = {};
    for (const [id, entry] of Object.entries(raw.done)) {
      if (!isRecord(entry) || typeof entry.ok !== 'boolean' || !isTime(entry.at)) return null;
      done[id] = { ok: entry.ok, at: entry.at };
    }
    const wrong = (raw.wrong as unknown[]).filter((id): id is string => typeof id === 'string');
    const data: ProgressData = { v: 1, done, wrong, updatedAt: raw.updatedAt };
    if (isTime(raw.resetAt)) data.resetAt = raw.resetAt;
    if (isTime(raw.wrongClearedAt)) data.wrongClearedAt = raw.wrongClearedAt;
    if (isRecord(raw.bookmarks)) {
      const bookmarks: NonNullable<ProgressData['bookmarks']> = {};
      for (const [id, mark] of Object.entries(raw.bookmarks)) {
        if (isRecord(mark) && typeof mark.on === 'boolean' && isTime(mark.at)) {
          bookmarks[id] = { on: mark.on, at: mark.at };
        }
      }
      if (Object.keys(bookmarks).length > 0) data.bookmarks = bookmarks;
    }
    return data;
  } catch {
    return null;
  }
}

/** The device's stash; `null` when there is none or it is unreadable (then it is removed). */
export function readStash(storage: KeyValueStorage, namespace: string): ProgressStash | null {
  const key = stashKey(namespace);
  const text = readKey(storage, key);
  if (text === null) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    raw = null;
  }
  if (
    !isRecord(raw) ||
    typeof raw.userId !== 'string' ||
    !isTime(raw.savedAt) ||
    !isRecord(raw.classes)
  ) {
    removeKey(storage, key);
    return null;
  }
  const classes: ProgressStash['classes'] = {};
  for (const [licenseClass, entry] of Object.entries(raw.classes)) {
    if (!isRecord(entry)) continue;
    const data = parseProgressData(entry.data);
    if (data === null) continue;
    classes[licenseClass] = isTime(entry.lastPutAt)
      ? { data, lastPutAt: entry.lastPutAt }
      : { data };
  }
  return { userId: raw.userId, savedAt: raw.savedAt, classes };
}

export function writeStash(
  storage: KeyValueStorage,
  namespace: string,
  stash: ProgressStash,
): void {
  writeKey(storage, stashKey(namespace), JSON.stringify(stash));
}

/** Older than the TTL (a `savedAt` ahead of the clock does not count as expired). */
export const stashExpired = (stash: ProgressStash, now: number, ttlMs = STASH_TTL_MS): boolean =>
  now - stash.savedAt > ttlMs;

/**
 * Deletes the device's stash when it is older than 7 days (or unreadable). Cheap: the sync calls it
 * when it starts, the app when it opens its progress storage. Returns whether a stash is left.
 */
export function sweepProgressStash(
  storage: KeyValueStorage,
  now: number,
  options: { namespace?: string; ttlMs?: number } = {},
): boolean {
  const namespace = options.namespace ?? 'dcs';
  const stash = readStash(storage, namespace);
  if (stash === null) return false;
  if (!stashExpired(stash, now, options.ttlMs)) return true;
  removeKey(storage, stashKey(namespace));
  return false;
}
